/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { ApplicantStore } from "./CommonForm";

export function buildApplicantEntry({
	role,
	member,
	answers,
	eligible,
	reducedToGI,
	faceAmount,
	giLimit
}: {
	role: "employee" | "spouse" | "child";
	member: any;
	answers: SIQuestionAnswer[];
	eligible: boolean;
	reducedToGI: boolean;
	faceAmount: number;
	giLimit: number;
}): ApplicantStore {
	return {
		role,
		answers,
		eligible,
		reducedToGI,
		member_object_id: member?._id,
		member_SSN: member?.SSN,
		child_name: role === "child" ? `${member?.first_name ?? ""} ${member?.last_name ?? ""}`.trim() : undefined,
		face_amount: faceAmount,
		gi_limit: giLimit,
		exceeds_gi: faceAmount > giLimit
	};
}

export function getHighestAllowedAmount(options: number[], giLimit: number): number {
	return options.filter((v) => v <= giLimit).reduce((max, v) => Math.max(max, v), 0);
}
export interface IFollowUp {
	answer: string;
	routing: "next" | "prev" | "end";
	questions: IQuestion[];
}
export interface IQuestion {
	id: string;
	text: string;
	answer_type: "yes_no" | "multiple_choice";
	have_to_answer: boolean;
	eligibility_answer: string | null;
	options: string[];
	routing: Record<string, "next" | "prev" | "end">;
	follow_ups: IFollowUp[];
}
export type SIApplicantType = "EMPLOYEE" | "SPOUSE" | "CHILD";
export interface SIQuestionAnswer {
	question_id: string;
	question_text: string;
	answer: string;
	eligible_answer?: string;
	// Set when this question came from a follow_ups branch, so a conditional
	// follow-up stays distinguishable from a question everyone is asked.
	parent_question_id?: string | null;
}

// ── Props: removed open/onClose, kept everything else ──
export interface SIQuestionsModalProps {
	questions: IQuestion[];
	relation: SIApplicantType;
	selectedAmount: number;
	siLimit: number;
	giLimit: number;
	onSubmit: (answers: SIQuestionAnswer[], hasAnyYes: boolean, eligible: boolean) => void;
	onReduceToGI?: () => void;
}

type QAItem = {
	question: IQuestion;
	answer?: string;
	list: IQuestion[];
	index: number;
	parent?: { list: IQuestion[]; index: number };
};

// ─── Design Tokens ────────────────────────────────────────────
const D = {
	violet: "#6366f1",
	violetDark: "#4f46e5",
	violetSoft: "rgba(99,102,241,0.08)",
	violetBorder: "rgba(99,102,241,0.2)",
	emerald: "#10b981",
	emeraldDark: "#059669",
	emeraldSoft: "rgba(16,185,129,0.08)",
	emeraldBorder: "rgba(16,185,129,0.2)",
	rose: "#f43f5e",
	roseDark: "#e11d48",
	roseSoft: "rgba(244,63,94,0.07)",
	roseBorder: "rgba(244,63,94,0.2)",
	amber: "#f59e0b",
	amberSoft: "rgba(245,158,11,0.08)",
	amberBorder: "rgba(245,158,11,0.22)",
	bg: "#ffffff",
	bgSubtle: "#f8fafc",
	bgMuted: "#f1f5f9",
	border: "rgba(15,23,42,0.08)",
	borderMid: "rgba(15,23,42,0.13)",
	textPrimary: "#0f172a",
	textSecondary: "#475569",
	textMuted: "#94a3b8",
	font: "'Inter','SF Pro Display',system-ui,-apple-system,sans-serif"
};

// ─── Keyframes ─────────────────────────────────────────────────
const cardIn = keyframes`
  from{opacity:0;transform:translateY(10px)}
  to{opacity:1;transform:translateY(0)}
`;
const checkStroke = keyframes`
  from{stroke-dashoffset:40}
  to{stroke-dashoffset:0}
`;
const dotPulse = keyframes`
  0%,100%{opacity:1} 50%{opacity:0.3}
`;

// When true, every question that defines an eligibility_answer must be answered
// with it before the questionnaire can be submitted. Questions with a null
// eligibility_answer accept whatever the user gives, either way.
const REQUIRE_ELIGIBLE_ANSWERS = false;

// ─── Logic Helpers ─────────────────────────────────────────────
function resolveRouting(q: IQuestion, a: string): "next" | "prev" | "end" {
	return q.routing?.[a] ?? q.routing?.[a.toLowerCase()] ?? "next";
}
function evaluateEligibility(conv: QAItem[]): boolean {
	return conv.every(
		({ question: q, answer: a }) =>
			!(q.eligibility_answer && a && a.toLowerCase() !== q.eligibility_answer.toLowerCase())
	);
}
function flattenAnswers(conv: QAItem[]): SIQuestionAnswer[] {
	return conv
		.filter((c) => !!c.answer)
		.map((c) => ({
			question_id: c.question.id,
			question_text: c.question.text,
			answer: c.answer!.toLowerCase(),
			eligible_answer: c.question.eligibility_answer?.toLowerCase() ?? undefined,
			parent_question_id: c.parent ? c.parent.list[c.parent.index].id : null
		}));
}

const GEneralQuestionsModal: React.FC<SIQuestionsModalProps> = ({
	questions,
	relation,
	selectedAmount,
	siLimit,
	giLimit,
	onSubmit,
	onReduceToGI
}) => {
	const bodyRef = useRef<HTMLDivElement>(null);
	const [conversation, setConversation] = useState<QAItem[]>([]);
	const [animKey, setAnimKey] = useState(0);

	useEffect(() => {
		if (questions.length > 0) {
			setConversation([{ question: questions[0], list: questions, index: 0 }]);
			setAnimKey((k) => k + 1);
		}
	}, [questions]);

	useEffect(() => {
		setTimeout(() => {
			bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
		}, 80);
	}, [conversation.length]);

	const handleAnswer = (stepIdx: number, answer: string) => {
		let updated = [...conversation];
		updated[stepIdx] = { ...updated[stepIdx], answer };
		updated = updated.slice(0, stepIdx + 1);

		const { question, list, index, parent } = updated[stepIdx];
		let nextQ: IQuestion | null = null,
			nextList = list,
			nextIndex = index,
			nextParent = parent;

		const followUp = question.follow_ups?.find((f) => f.answer.toLowerCase() === answer.toLowerCase());

		if (followUp?.questions?.length) {
			nextList = followUp.questions;
			nextIndex = 0;
			nextQ = followUp.questions[0];
			nextParent = { list, index };
		} else {
			const route = resolveRouting(question, answer);
			if (route === "end" || route === "prev") {
				setConversation(updated);
				return;
			}
			if (list[index + 1]) {
				nextIndex = index + 1;
				nextQ = list[nextIndex];
			} else if (parent) {
				const pfu = parent.list[parent.index].follow_ups?.find(
					(f) => f.answer.toLowerCase() === updated[stepIdx - (index + 1)]?.answer?.toLowerCase()
				);
				const br = pfu?.routing ?? "next";
				if (br === "end" || br === "prev") {
					setConversation(updated);
					return;
				}
				if (parent.list[parent.index + 1]) {
					nextList = parent.list;
					nextIndex = parent.index + 1;
					nextQ = parent.list[nextIndex];
					nextParent = undefined;
				}
			}
		}

		if (nextQ) {
			updated.push({ question: nextQ, list: nextList, index: nextIndex, parent: nextParent });
		}
		setConversation(updated);
	};

	const handleReset = () => {
		setConversation(questions.length > 0 ? [{ question: questions[0], list: questions, index: 0 }] : []);
		setAnimKey((k) => k + 1);
	};

	const answered = conversation.filter((c) => c.answer).length;
	const total = conversation.length;
	const progress = total === 0 ? 0 : Math.round((answered / total) * 100);

	const lastItem = conversation[conversation.length - 1];
	const isComplete =
		conversation.length > 0 &&
		!!lastItem?.answer &&
		(() => {
			const fu = lastItem.question.follow_ups?.find(
				(f) => f.answer.toLowerCase() === lastItem.answer!.toLowerCase()
			);
			if (fu?.questions?.length) return false;
			const r = resolveRouting(lastItem.question, lastItem.answer!);
			if (r === "end") return true;
			return !lastItem.list[lastItem.index + 1] && !lastItem.parent;
		})();

	const eligible = evaluateEligibility(conversation);
	const isIneligible = !eligible;

	// In strict mode a completed but ineligible questionnaire cannot be submitted
	const blockedByEligibility = REQUIRE_ELIGIBLE_ANSWERS && isComplete && isIneligible;
	const canSubmit = isComplete && !blockedByEligibility;

	const getStepStatus = (item: QAItem) => {
		if (!item.answer) return "neutral";
		if (!item.question.eligibility_answer) return "correct";
		return item.answer.toLowerCase() === item.question.eligibility_answer.toLowerCase() ? "correct" : "incorrect";
	};

	const checkOptionEligibility = (idx: number, opt: string) => {
		const tmp = conversation.map((item, i) => (i === idx ? { ...item, answer: opt } : { ...item }));
		return evaluateEligibility(tmp.slice(0, idx + 1));
	};

	return (
		<Box
			sx={{
				width: "100%",
				borderRadius: "20px",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				background: D.bg,
				boxShadow: "0 0 0 1px rgba(15,23,42,0.06), 0 8px 32px rgba(2,8,23,0.08)"
			}}
		>
			{/* ── HEADER ── */}
			<Box
				sx={{
					px: { xs: 2.5, sm: 3.5 },
					pt: 3,
					pb: 2.5,
					borderBottom: `1px solid ${D.border}`,
					background: D.bg,
					flexShrink: 0
				}}
			>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between">
					<Stack direction="row" spacing={1.75} alignItems="center">
						<Box
							sx={{
								width: 44,
								height: 44,
								borderRadius: "12px",
								background: D.violetSoft,
								border: `1px solid ${D.violetBorder}`,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0
							}}
						>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
								<path
									d="M10 2L3 5.5v5c0 3.59 3.06 6.95 7 7.5 3.94-.55 7-3.91 7-7.5v-5L10 2z"
									stroke={D.violet}
									strokeWidth="1.5"
									strokeLinejoin="round"
								/>
								<path
									d="M7 10l2 2 4-4"
									stroke={D.violet}
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</Box>
						<Box>
							<Typography
								sx={{
									fontFamily: D.font,
									fontWeight: 600,
									fontSize: "1.05rem",
									color: D.textPrimary,
									lineHeight: 1.3
								}}
							>
								Health Questionnaire
							</Typography>
							{/* <Typography sx={{ fontFamily: D.font, fontSize: "0.78rem", color: D.textMuted, mt: 0.2 }}>
								Simplified Issue underwriting review
							</Typography> */}
						</Box>
					</Stack>
				</Stack>
			</Box>

			{/* ── BODY ── */}
			<Box
				ref={bodyRef}
				sx={{
					flex: 1,
					overflowY: "auto",
					px: { xs: 2.5, sm: 3.5 },
					py: 3,
					display: "flex",
					flexDirection: "column",
					gap: 2,
					background: D.bgSubtle,
					"&::-webkit-scrollbar": { width: "4px" },
					"&::-webkit-scrollbar-thumb": { background: D.borderMid, borderRadius: "99px" },
					"&::-webkit-scrollbar-track": { background: "transparent" }
				}}
			>
				{/* GI Alert */}

				{/* Questions */}
				{conversation.map((item, i) => (
					<QuestionCard
						key={`${animKey}-${i}`}
						item={item}
						index={i}
						status={getStepStatus(item)}
						onAnswer={(ans) => handleAnswer(i, ans)}
						animDelay={i * 40}
						checkOptionEligibility={checkOptionEligibility}
					/>
				))}

				{/* Completion banner */}
				{isComplete && (
					<Box
						sx={{
							borderRadius: "14px",
							border: `1px solid ${isIneligible ? D.roseBorder : D.emeraldBorder}`,
							background: isIneligible ? D.roseSoft : D.emeraldSoft,
							p: 2.5,
							display: "flex",
							gap: 2,
							alignItems: "flex-start",
							animation: `${cardIn} 0.35s ease`
						}}
					>
						<Box
							sx={{
								width: 40,
								height: 40,
								borderRadius: "12px",
								flexShrink: 0,
								background: isIneligible ? D.rose : D.emerald,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								boxShadow: `0 4px 14px ${isIneligible ? "rgba(244,63,94,0.3)" : "rgba(16,185,129,0.3)"}`
							}}
						>
							{isIneligible ? (
								<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
									<path
										d="M9 2.5L16 14H2L9 2.5z"
										stroke="white"
										strokeWidth="1.5"
										fill="none"
										strokeLinejoin="round"
									/>
									<line
										x1="9"
										y1="7"
										x2="9"
										y2="10.5"
										stroke="white"
										strokeWidth="1.5"
										strokeLinecap="round"
									/>
									<circle cx="9" cy="12.5" r="0.75" fill="white" />
								</svg>
							) : (
								<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
									<path
										d="M4 9l3.5 3.5L14 5"
										stroke="white"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeDasharray="40"
										style={{
											animation: `${checkStroke} 0.45s 0.1s ease forwards`,
											strokeDashoffset: 40
										}}
									/>
								</svg>
							)}
						</Box>
						<Box>
							<Typography
								sx={{
									fontFamily: D.font,
									fontWeight: 600,
									fontSize: "0.95rem",
									color: isIneligible ? D.roseDark : D.emeraldDark,
									mb: 0.4
								}}
							>
								{isIneligible ? "Full underwriting required" : "Simplified Issue approved"}
							</Typography>
							<Typography
								sx={{
									fontFamily: D.font,
									fontSize: "0.8rem",
									color: D.textSecondary,
									lineHeight: 1.65
								}}
							>
								{isIneligible
									? "Based on your responses, full underwriting review is required. A representative may contact you for additional information. Please note: all answers provided are subject to verification. Any material misrepresentation or inaccurate information may result in denial of claims, reduction of benefits, or cancellation of coverage in accordance with your policy terms."
									: "Your responses qualify for Simplified Issue processing. No additional medical requirements are needed at this time. Please note: your application remains subject to standard underwriting review. Any inaccurate or incomplete information provided may affect your eligibility, benefit amount, or claim outcomes as outlined in your policy terms."}
							</Typography>
						</Box>
					</Box>
				)}

				<Box sx={{ height: 4 }} />
			</Box>

			{/* ── FOOTER ── */}
			<Box
				sx={{
					px: { xs: 2.5, sm: 3.5 },
					py: 2,
					borderTop: `1px solid ${D.border}`,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					background: D.bg,
					gap: 1.5,
					flexWrap: "wrap",
					flexShrink: 0
				}}
			>
				<Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1, overflow: "hidden" }}>
					{Array.from({ length: Math.min(total, 24) }).map((_, i) => (
						<Box
							key={i}
							sx={{
								height: 4,
								width: i < answered ? 20 : 4,
								borderRadius: "99px",
								background: i < answered ? D.violet : D.borderMid,
								transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
								flexShrink: 0
							}}
						/>
					))}
				</Stack>

				<Stack direction="row" spacing={1} alignItems="center">
					<GhostBtn onClick={handleReset}>Reset</GhostBtn>
					{/* {onReduceToGI && (
						<OutlineBtn onClick={onReduceToGI}>
							<TrendingDownIcon sx={{ fontSize: 14 }} />
							Reduce to GI
						</OutlineBtn>
					)} */}
					{/* Submit only appears once every question is answered */}
					{canSubmit && (
						<PrimaryBtn
							onClick={() => {
								if (!canSubmit) return;
								const eligible = evaluateEligibility(conversation);
								const answers = flattenAnswers(conversation);
								onSubmit(
									answers,
									answers.some((a) => a.answer === "yes"),
									eligible
								);
							}}
						>
							Submit
						</PrimaryBtn>
					)}
				</Stack>
			</Box>
		</Box>
	);
};

export default GEneralQuestionsModal;

// ─── Buttons ───────────────────────────────────────────────────
const btnBase = {
	display: "inline-flex",
	alignItems: "center",
	gap: 0.6,
	px: 2,
	py: 0.85,
	borderRadius: "10px",
	fontSize: "0.8rem",
	fontFamily: D.font,
	fontWeight: 500,
	cursor: "pointer",
	transition: "all .18s",
	"&:active": { transform: "scale(0.97)" }
};

function GhostBtn({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
	return (
		<Box
			component="button"
			onClick={onClick}
			sx={{
				...btnBase,
				background: "transparent",
				border: `1px solid ${D.border}`,
				color: D.textSecondary,
				"&:hover": { background: D.bgMuted, color: D.textPrimary, borderColor: D.borderMid }
			}}
		>
			{children}
		</Box>
	);
}

function OutlineBtn({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
	return (
		<Box
			component="button"
			onClick={onClick}
			sx={{
				...btnBase,
				background: "transparent",
				border: `1px solid ${D.violetBorder}`,
				color: D.violet,
				"&:hover": { background: D.violetSoft }
			}}
		>
			{children}
		</Box>
	);
}

function PrimaryBtn({
	children,
	onClick,
	disabled
}: {
	children: React.ReactNode;
	onClick?: () => void;
	disabled?: boolean;
}) {
	return (
		<Box
			component="button"
			onClick={onClick}
			disabled={disabled}
			sx={{
				...btnBase,
				background: disabled ? D.bgMuted : D.violet,
				border: "none",
				color: disabled ? D.textMuted : "#fff",
				boxShadow: disabled ? "none" : "0 4px 14px rgba(99,102,241,0.28)",
				cursor: disabled ? "not-allowed" : "pointer",
				"&:hover": disabled ? {} : { background: D.violetDark, boxShadow: "0 6px 20px rgba(99,102,241,0.38)" },
				"&:active": { transform: disabled ? "none" : "scale(0.97)" }
			}}
		>
			{children}
		</Box>
	);
}

// ─── QuestionCard ──────────────────────────────────────────────
function QuestionCard({
	item,
	index,
	status,
	onAnswer,
	animDelay,
	checkOptionEligibility
}: {
	item: QAItem;
	index: number;
	status: "correct" | "incorrect" | "neutral";
	onAnswer: (ans: string) => void;
	animDelay: number;
	checkOptionEligibility: (i: number, opt: string) => boolean;
}) {
	const isFollowUp = !!item.parent;
	const answered = !!item.answer;

	const borderColor =
		status === "correct"
			? D.emeraldBorder
			: status === "incorrect"
			? D.roseBorder
			: isFollowUp
			? D.amberBorder
			: D.border;
	const badgeBg = !answered
		? isFollowUp
			? D.amber
			: D.bgMuted
		: status === "correct"
		? D.emerald
		: status === "incorrect"
		? D.rose
		: D.violet;
	const badgeColor = !answered && !isFollowUp ? D.textMuted : "#fff";

	return (
		<Box sx={{ ml: isFollowUp ? 3 : 0, opacity: 0, animation: `${cardIn} 0.3s ease ${animDelay}ms forwards` }}>
			{isFollowUp && (
				<Stack direction="row" spacing={0.75} alignItems="center" mb={0.75} ml={0.5}>
					<svg width="12" height="10" viewBox="0 0 12 10" fill="none">
						<path d="M1 1v4a2 2 0 002 2h8" stroke={D.amber} strokeWidth="1.3" strokeLinecap="round" />
						<path
							d="M9 5l2 2-2 2"
							stroke={D.amber}
							strokeWidth="1.3"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
					<Typography
						sx={{
							fontFamily: D.font,
							fontSize: "0.67rem",
							fontWeight: 600,
							color: D.amber,
							letterSpacing: "0.07em",
							textTransform: "uppercase"
						}}
					>
						Follow-up
					</Typography>
				</Stack>
			)}

			<Box
				sx={{
					borderRadius: "14px",
					border: `1px solid ${borderColor}`,
					background: D.bg,
					p: { xs: 2, sm: 2.5 },
					boxShadow: answered ? "none" : "0 1px 4px rgba(15,23,42,0.04)",
					transition: "border-color .25s, box-shadow .25s"
				}}
			>
				<Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
					<Box
						sx={{
							width: 28,
							height: 28,
							borderRadius: "8px",
							flexShrink: 0,
							background: badgeBg,
							color: badgeColor,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "0.72rem",
							fontWeight: 700,
							fontFamily: D.font,
							transition: "all .25s",
							boxShadow:
								answered && status !== "neutral"
									? `0 2px 8px ${
											status === "correct" ? "rgba(16,185,129,0.28)" : "rgba(244,63,94,0.28)"
									  }`
									: "none"
						}}
					>
						{index + 1}
					</Box>
					<Typography
						sx={{
							fontFamily: D.font,
							fontSize: "0.9rem",
							fontWeight: answered ? 400 : 500,
							color: answered ? D.textSecondary : D.textPrimary,
							lineHeight: 1.65,
							pt: 0.2,
							transition: "color .2s"
						}}
					>
						{item.question.text}
					</Typography>
				</Stack>

				<Box pl={{ xs: 0, sm: "44px" }}>
					{item.question.answer_type === "yes_no" && (
						<Stack direction="row" spacing={1}>
							{(["Yes", "No"] as const).map((opt) => {
								const eligible = item.question.eligibility_answer
									? opt.toLowerCase() === item.question.eligibility_answer.toLowerCase()
									: true;
								return (
									<PillOption
										key={opt}
										label={opt}
										selected={item.answer === opt}
										variant={eligible ? "approve" : "decline"}
										onClick={() => onAnswer(opt)}
									/>
								);
							})}
						</Stack>
					)}

					{item.question.answer_type === "multiple_choice" && (
						<Stack spacing={0.75}>
							{item.question.options.map((opt) => (
								<PillOption
									key={opt}
									label={opt}
									selected={item.answer === opt}
									variant="neutral"
									onClick={() => onAnswer(opt)}
									block
								/>
							))}
						</Stack>
					)}

					{item.question.have_to_answer && !item.answer && (
						<Stack direction="row" spacing={0.75} alignItems="center" mt={1.25}>
							<Box
								sx={{
									width: 6,
									height: 6,
									borderRadius: "50%",
									background: D.amber,
									animation: `${dotPulse} 1.6s ease-in-out infinite`
								}}
							/>
							<Typography
								sx={{ fontFamily: D.font, fontSize: "0.72rem", color: D.amber, fontWeight: 500 }}
							>
								Required to continue
							</Typography>
						</Stack>
					)}
				</Box>
			</Box>
		</Box>
	);
}

// ─── PillOption ────────────────────────────────────────────────
function PillOption({
	label,
	selected,
	variant,
	onClick,
	block
}: {
	label: string;
	selected: boolean;
	variant: "approve" | "decline" | "neutral";
	onClick: () => void;
	block?: boolean;
}) {
	const active = {
		approve: { bg: D.emerald, border: D.emerald, color: "#fff", shadow: "0 3px 10px rgba(16,185,129,0.3)" },
		decline: { bg: D.rose, border: D.rose, color: "#fff", shadow: "0 3px 10px rgba(244,63,94,0.3)" },
		neutral: { bg: D.violet, border: D.violet, color: "#fff", shadow: "0 3px 10px rgba(99,102,241,0.28)" }
	};
	const idle = {
		approve: { border: D.emeraldBorder, color: D.emeraldDark, hover: D.emeraldSoft },
		decline: { border: D.roseBorder, color: D.roseDark, hover: D.roseSoft },
		neutral: { border: D.border, color: D.textSecondary, hover: D.bgMuted }
	};

	return (
		<Box
			component="button"
			onClick={onClick}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0.75,
				px: 2,
				py: 0.85,
				borderRadius: "99px",
				border: "1px solid",
				width: block ? "100%" : "auto",
				fontSize: "0.82rem",
				fontWeight: selected ? 600 : 400,
				fontFamily: D.font,
				cursor: "pointer",
				transition: "all 0.18s ease",
				...(selected
					? {
							background: active[variant].bg,
							borderColor: active[variant].border,
							color: active[variant].color,
							boxShadow: active[variant].shadow
					  }
					: {
							background: "transparent",
							borderColor: idle[variant].border,
							color: idle[variant].color,
							"&:hover": { background: idle[variant].hover }
					  }),
				"&:active": { transform: "scale(0.97)" }
			}}
		>
			<Box
				component="span"
				sx={{
					width: 7,
					height: 7,
					borderRadius: "50%",
					flexShrink: 0,
					background: selected ? "rgba(255,255,255,0.55)" : "transparent",
					border: `1.5px solid ${selected ? "rgba(255,255,255,0.65)" : "currentColor"}`,
					transition: "all .18s"
				}}
			/>
			{label}
		</Box>
	);
}
